import { useState, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../../../../services/api'
import { useDisclosure } from '@chakra-ui/react'
import { useQuery, useMutation, useQueryClient } from 'react-query'

export function UseCadastroDeProdutoAPI(toast) {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  const isUpdatingFromUrl = useRef(false)
  const isMounted = useRef(false)

  const [produtoEmEdicao, setProdutoEmEdicao] = useState(null)

  const modalStatus = useDisclosure()
  const modalAdicionar = useDisclosure()
  const modalEditar = useDisclosure()

  // Adicionar estado para acompanhar se o modo de edição é step-by-step
  const [isModoStepByStep, setIsModoStepByStep] = useState(true)

  const [filtros, setFiltros] = useState({
    fornecedor: searchParams.get('fornecedor') || '',
    pendente: searchParams.get('pendente') || '',
    excluido: searchParams.get('excluido') || '',
    filtrar_por: searchParams.get('filtrar_por') || 'dt_api',
    ordenacao: searchParams.get('ordenacao') || 'desc',
  })

  const [paginacao, setPaginacao] = useState({
    pagina: parseInt(searchParams.get('pagina')) || 1,
    limite: parseInt(searchParams.get('limite')) || 10,
    pagina_atual: parseInt(searchParams.get('pagina')) || 1,
    ultima_pagina: 1,
    total: 0,
    por_pagina: parseInt(searchParams.get('limite')) || 10,
  })

  const [tiposImpressao, setTiposImpressao] = useState([
    { id: 1, nome: 'Silk Screen' },
    { id: 2, nome: 'Tampografia' },
    { id: 3, nome: 'Laser' },
    { id: 4, nome: 'Sublimação' },
    { id: 5, nome: 'Transfer' },
  ])

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    if (isUpdatingFromUrl.current) {
      return
    }

    const params = new URLSearchParams()

    if (filtros.fornecedor) params.set('fornecedor', filtros.fornecedor)
    if (filtros.pendente) params.set('pendente', filtros.pendente)
    if (filtros.excluido) params.set('excluido', filtros.excluido)
    if (filtros.filtrar_por !== 'dt_api') params.set('filtrar_por', filtros.filtrar_por)
    if (filtros.ordenacao !== 'desc') params.set('ordenacao', filtros.ordenacao)

    if (paginacao.pagina !== 1) params.set('pagina', paginacao.pagina.toString())
    if (paginacao.limite !== 10) params.set('limite', paginacao.limite.toString())

    setSearchParams(params, { replace: true })
  }, [filtros, paginacao.pagina, paginacao.limite, setSearchParams])

  useEffect(() => {
    if (!isMounted.current) return

    isUpdatingFromUrl.current = true

    const urlFornecedor = searchParams.get('fornecedor')
    const urlPendente = searchParams.get('pendente')
    const urlExcluido = searchParams.get('excluido')
    const urlFiltrarPor = searchParams.get('filtrar_por')
    const urlOrdenacao = searchParams.get('ordenacao')
    const urlPagina = searchParams.get('pagina') ? parseInt(searchParams.get('pagina')) : 1
    const urlLimite = searchParams.get('limite') ? parseInt(searchParams.get('limite')) : 10

    const newFiltros = {
      fornecedor: urlFornecedor || '',
      pendente: urlPendente || '',
      excluido: urlExcluido || '',
      filtrar_por: urlFiltrarPor || 'dt_api',
      ordenacao: urlOrdenacao || 'desc',
    }

    if (JSON.stringify(newFiltros) !== JSON.stringify(filtros)) {
      setFiltros(newFiltros)
    }

    if (urlPagina !== paginacao.pagina || urlLimite !== paginacao.limite) {
      setPaginacao((prev) => ({
        ...prev,
        pagina: urlPagina,
        pagina_atual: urlPagina,
        limite: urlLimite,
      }))
    }

    setTimeout(() => {
      isUpdatingFromUrl.current = false
    }, 0)
  }, [searchParams, filtros, paginacao.pagina, paginacao.limite])

  const {
    data: produtosData,
    isLoading: isLoadingProdutos,
    refetch: refetchProdutos,
  } = useQuery(
    ['produtos', filtros, paginacao.pagina, paginacao.limite],
    async () => {
      const params = {
        pagina: paginacao.pagina,
        limite: paginacao.limite,
        filtrar_por: filtros.filtrar_por,
        ordenacao: filtros.ordenacao,
      }

      if (filtros.fornecedor) {
        // Certifique-se de que o valor é enviado como inteiro
        params.fornecedor = parseInt(filtros.fornecedor)
      }

      if (filtros.pendente) {
        params.pendente = filtros.pendente
      }

      if (filtros.excluido) {
        params.excluido = filtros.excluido
      }

      const response = await api.get('/produto/pre-cadastro/produtos', { params })

      setPaginacao((prev) => ({
        ...prev,
        total: response.data.paginacao?.total || 0,
        ultima_pagina: response.data.paginacao?.ultima_pagina || 1,
        por_pagina: response.data.paginacao?.por_pagina || 10,
      }))

      return response.data
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      retry: 1, // Limitar tentativas para evitar loops
      onError: (error) => {
        console.error('Erro ao buscar produtos:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Não foi possível carregar os produtos.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const {
    data: statusData,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery(
    ['status-produtos'],
    async () => {
      const response = await api.get('/produto/pre-cadastro/status')
      return response.data
    },
    {
      onError: (error) => {
        console.error('Erro ao buscar status:', error)

        const errorMessage = error.response?.data?.message || 'Não foi possível carregar os dados de status.'

        toast({
          title: 'Erro',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
      enabled: false,
      refetchOnWindowFocus: false,
    }
  )

  const { isLoading: isLoadingProdutoDetalhe, refetch: refetchProdutoDetalhe } = useQuery(
    ['produto-detalhe', produtoEmEdicao?.id_produto],
    async () => {
      if (!produtoEmEdicao?.id_produto) return null

      const response = await api.get(`/produto/pre-cadastro/produtos/${produtoEmEdicao.id_produto}`)
      setProdutoEmEdicao(response.data)
      return response.data
    },
    {
      onError: (error) => {
        console.error('Erro ao buscar detalhes do produto:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível carregar os detalhes do produto.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
      enabled: false,
      refetchOnWindowFocus: false,
    }
  )

  const adicionarProdutosMutation = useMutation(
    async (fornecedor) => {
      return await api.post('/produto/pre-cadastro/adicionar-produtos', { fornecedor })
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Produtos adicionados com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
        modalAdicionar.onClose()
      },
      onError: (error) => {
        console.error('Erro ao adicionar produtos:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível adicionar os produtos.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const adicionarFotoMutation = useMutation(
    async (formData) => {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      return await api.post('/produto/pre-cadastro/adicionar-foto', formData, config)
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Fotos adicionadas com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
      },
      onError: (error) => {
        console.error('Erro ao adicionar fotos:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Não foi possível adicionar as fotos.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const alterarPrioridadeMutation = useMutation(
    async (id_produto) => {
      return await api.post('/produto/pre-cadastro/prioridade-produto', { id_produto })
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Prioridade alterada com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
      },
      onError: (error) => {
        console.error('Erro ao alterar prioridade:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível alterar a prioridade.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const editarProdutoMutation = useMutation(
    async (dados) => {
      return await api.put(`/produto/pre-cadastro/editar-produto`, dados)
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Produto editado com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
        modalEditar.onClose()
        setProdutoEmEdicao(null)
      },
      onError: (error) => {
        console.error('Erro ao editar produto:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Não foi possível editar o produto.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const deletarProdutoMutation = useMutation(
    async ({ id_produto, id_usuario, descricao }) => {
      return await api.put(`/produto/pre-cadastro/deletar-produto`, { id_usuario, id_produto, obs: descricao })
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Produto excluído com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
      },
      onError: (error) => {
        console.error('Erro ao deletar produto:', error)
        toast({
          title: 'Erro',
          description: 'Não foi possível excluir o produto.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const editarObservacaoMutation = useMutation(
    async ({ id_produto, obs }) => {
      return await api.put(`/produto/pre-cadastro/editar-obs`, { id_produto, obs })
    },
    {
      onSuccess: () => {
        toast({
          title: 'Sucesso',
          description: 'Observação atualizada com sucesso.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        queryClient.invalidateQueries(['produtos'])
      },
      onError: (error) => {
        console.error('Erro ao atualizar observação:', error)
        toast({
          title: 'Erro',
          description: error.response?.data?.message || 'Não foi possível atualizar a observação.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      },
    }
  )

  const isLoading =
    isLoadingProdutos ||
    isLoadingStatus ||
    isLoadingProdutoDetalhe ||
    adicionarProdutosMutation.isLoading ||
    adicionarFotoMutation.isLoading ||
    alterarPrioridadeMutation.isLoading ||
    editarProdutoMutation.isLoading ||
    deletarProdutoMutation.isLoading ||
    editarObservacaoMutation.isLoading // Adicionado aqui

  const handleChangeFiltro = useCallback((campo, valor) => {
    setFiltros((prev) => {
      if (prev[campo] === valor) return prev

      setPaginacao((prevPag) => ({
        ...prevPag,
        pagina: 1,
        pagina_atual: 1,
      }))

      return { ...prev, [campo]: valor }
    })
  }, [])

  const handleChangePaginacao = useCallback((novaPagina) => {
    setPaginacao((prev) => ({
      ...prev,
      pagina: novaPagina,
      pagina_atual: novaPagina,
    }))
  }, [])

  // Função para alterar o limite de itens por página
  const handleChangeLimite = useCallback((novoLimite) => {
    setPaginacao((prev) => ({
      ...prev,
      pagina: 1, // Voltar para a primeira página ao mudar o limite
      pagina_atual: 1,
      limite: novoLimite,
      por_pagina: novoLimite,
    }))
  }, [])

  const handleBuscarProdutos = useCallback(() => {
    setPaginacao((prev) => ({
      ...prev,
      pagina: 1,
      pagina_atual: 1,
    }))
  }, [])

  const handleAbrirModalStatus = () => {
    refetchStatus()
    modalStatus.onOpen()
  }

  const handleFecharModalStatus = () => {
    modalStatus.onClose()
  }

  const handleAbrirModalAdicionar = () => {
    modalAdicionar.onOpen()
  }

  const handleFecharModalAdicionar = () => {
    modalAdicionar.onClose()
  }

  const handleAbrirModalEditar = useCallback(
    async (id_produto) => {
      setProdutoEmEdicao({ id_produto })
      await refetchProdutoDetalhe()
      modalEditar.onOpen()
    },
    [refetchProdutoDetalhe, modalEditar]
  )

  const handleFecharModalEditar = () => {
    setProdutoEmEdicao(null)
    modalEditar.onClose()
  }

  const handleAdicionarProdutos = (fornecedor) => {
    adicionarProdutosMutation.mutate(fornecedor)
  }

  const handleAdicionarFoto = (formData) => {
    return adicionarFotoMutation.mutateAsync(formData)
  }

  const handleAlterarPrioridade = (id_produto) => {
    alterarPrioridadeMutation.mutate(id_produto)
  }

  const handleSalvarEdicaoProduto = async (dadosProduto) => {
    try {
      // Invalidar queries para garantir que os dados serão atualizados
      queryClient.invalidateQueries(['produtos'])

      // Fechar modal e limpar produto em edição
      modalEditar.onClose()
      setProdutoEmEdicao(null)

      // Exibir toast de sucesso
      toast({
        title: 'Sucesso',
        description: 'Produto editado com sucesso.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      return true
    } catch (error) {
      console.error('Erro ao processar edição do produto:', error)
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Não foi possível editar o produto.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw error
    }
  }

  const handleDeletarProduto = async (id_produto, id_usuario, descricao) => {
    await deletarProdutoMutation.mutateAsync({ id_produto, id_usuario, descricao })
  }

  const handleEditarObservacao = async (id_produto, obs) => {
    await editarObservacaoMutation.mutateAsync({ id_produto, obs })
  }

  return {
    produtos: produtosData?.produtos || [],
    filtros,
    isLoading,
    paginacao,
    modalStatus,
    modalAdicionar,
    modalEditar,
    statusData,
    produtoEmEdicao,
    tiposImpressao,
    isModoStepByStep,

    handleChangeFiltro,
    handleChangePaginacao,
    handleChangeLimite,
    handleBuscarProdutos,
    handleAbrirModalStatus,
    handleFecharModalStatus,
    handleAbrirModalAdicionar,
    handleFecharModalAdicionar,
    handleAbrirModalEditar,
    handleFecharModalEditar,
    handleAdicionarProdutos,
    handleAdicionarFoto,
    handleAlterarPrioridade,
    handleSalvarEdicaoProduto,
    handleDeletarProduto,
    handleEditarObservacao,
  }
}
